import Controller from '@ember/controller';
import EmObject, { action, computed } from '@ember/object';
import { A } from '@ember/array';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { NewIntegration } from 'cio/components/integrations/new-integration-form';
import { pruneObject } from 'cio/utils/objects';
import ApiService from 'cio/services/api';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import AuthService from 'cio/services/auth';
import MessageSettingsService from 'cio/services/message-settings';
import urlBuilder from 'cio/utils/url-builder';
import INTEGRATIONS_METADATA, {
  FEATURE_FLAGGED_INTEGRATIONS,
  IntegrationIDs,
  IntegrationType,
} from 'cio/utils/integrations/static-integrations';
import { all, hash, hashSettled } from 'rsvp';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import FeaturesService from 'ember-feature-flags/services/features';
import { SqlImportDatabase } from 'cio/utils/sql-import';
import CustomFormsService from 'cio/services/custom-forms';
import ApiCredentialsService from 'cio/services/api-credentials';
import { AdNetworkType } from 'cio/utils/ad-audiences';
import AudienceModel from 'cio/models/audience';
import SqlImportModel from 'cio/models/sql-import';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';

enum NewIntegrationModalState {
  None = 'NONE',
  Form = 'FORM',
  Thanks = 'THANKS',
}

export default class IntegrationsBaseController extends Controller {
  @service api!: ApiService;
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service customForms!: CustomFormsService;
  @service features!: FeaturesService;
  @service messageSettings!: MessageSettingsService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service apiCredentials!: ApiCredentialsService;
  @service store!: DS.Store;

  // we're initializing "query" and "selectedCategory" and "selectedTypeQP"
  // query params to null, but then adding setters/getters to avoid using
  // them as strings - with "" as empty value
  //
  // this is done to avoid adding "query=&selectedCategory=&selectedType="
  // query params to the URL when they're set to empty values ("")
  @tracked queryQP: string | null = null;
  @tracked selectedCategoryQP: string | null = null;
  @tracked selectedTypeQP: string | null = null;

  get query() {
    return this.queryQP ?? '';
  }
  set query(val: string) {
    this.queryQP = val === '' ? null : val;
  }

  get selectedCategory() {
    return this.selectedCategoryQP ?? '';
  }
  set selectedCategory(val: string) {
    this.selectedCategoryQP = val === '' ? null : val;
  }

  get selectedType() {
    return this.selectedTypeQP ?? '';
  }
  set selectedType(val: string) {
    this.selectedTypeQP = val === '' ? null : val;
  }

  modal = NewIntegrationModalState.None;
  newIntegrationRequest = EmObject.create<any, Partial<NewIntegration>>({});
  readonly public!: boolean;
  readonly model!: any;

  trackFilterEvent(from: 'filter' | 'search' | 'type') {
    if (from === 'search' && !this.query) {
      return;
    }

    let trackData = {
      from: this.from,
      category: this.selectedCategory || null,
      type: this.selectedType || null,
      query: this.query || null,
    };

    // This will be either `list_searched` or `list_filtered`, but TS can't infer that
    this.analytics.track('integration', `list_${from}ed` as any, pruneObject(trackData));
  }

  @computed('public')
  get from() {
    return this.public ? 'Public' : 'In-App';
  }

  @computed('newIntegrationRequest.name')
  get calendlyUrl() {
    let params: Record<string, string | undefined | null> = {
      utm_source: this.from,
      utm_campaign: 'New',
      utm_term: this.newIntegrationRequest.name,
      utm_medium: this.auth?.user?.id ?? this.analytics.getAnonId(),
      salesforce_uuid: this.account?.current?.id,
      utm_content: this.selectedCategory,
    };

    return urlBuilder('https://calendly.com/customer-io-integrations/customer-io-integration-request', params);
  }

  @computed('fetchIntegrations.last.value')
  get categories() {
    return this.integrations.uniqBy('category').mapBy('category').sort();
  }

  @computed('fetchIntegrations.lastSuccessful.value')
  get integrations() {
    let extraIntegrations = this.fetchIntegrations.lastSuccessful?.value?.toArray() ?? [];

    let integrations = A([...Object.values(INTEGRATIONS_METADATA), ...extraIntegrations]);

    integrations = integrations.filter((i) => {
      let featureFlag = FEATURE_FLAGGED_INTEGRATIONS[i.id];

      if (featureFlag == null) {
        return true;
      }

      return this.features.isEnabled(featureFlag);
    });

    return integrations;
  }

  @computed('query', 'selectedCategory', 'selectedType', 'integrations')
  get filteredIntegrations() {
    return this.integrations.filter(({ id, name, category, publicType }) => {
      if ([IntegrationIDs.cdpOut, IntegrationIDs.cdpIn].includes(id)) {
        return false;
      }

      let searchableType: string = publicType;
      switch (publicType) {
        case IntegrationType.connectPartner: {
          searchableType = 'Connect Partner';
          break;
        }
        case IntegrationType.existing: {
          searchableType = 'Existing';
          break;
        }
      }
      let searchable = `${name}|${category}|${searchableType}`.toLowerCase();
      return (
        (searchable.includes(this.query.toLowerCase()) as boolean) &&
        category.includes(this.selectedCategory) &&
        (this.selectedType != null && this.selectedType !== '' ? publicType === this.selectedType : true)
      );
    });
  }

  integrationsSort = ['forceOrder', 'name'];
  @sort('filteredIntegrations', 'integrationsSort') sortedIntegrations!: Record<string, any>[];

  fetchIntegrations = task({ keepLatest: true }, async () => {
    return this.store.findAll('integration');
  });

  fetchCampaigns = task({ keepLatest: true }, async () => {
    return this.store.findAll('campaign');
  });

  reportingWebhooks = task({ keepLatest: true }, async () => {
    try {
      let webhooks = await this.store.findAll('webhook');

      return webhooks;
    } catch {
      return null;
    }
  });

  segmentOut = task({ keepLatest: true }, async () => {
    try {
      await this.api.request('integrations/segdotcom', {
        ignoreErrors: true,
      });
      return true;
    } catch {
      return false;
    }
  });

  fetchCredentials = task({ keepLatest: true }, async () => {
    return hash({
      audienceCredentials: this.store.findAll('audience-credential'),
    });
  });

  fetchAdAudiences = task({ keepLatest: true }, async () => {
    let adAudiences = await this.store.findAll('audience');

    return {
      facebook: adAudiences.filterBy('isFacebook'),
      adwords: adAudiences.filterBy('isAdwords'),
    } as Record<AdNetworkType, AudienceModel[]>;
  });

  fetchMparticle = task({ keepLatest: true }, async () => {
    try {
      return await this.store.queryRecord('integrations/mparticle', {});
    } catch (e) {
      return false;
    }
  });

  fetchCdpOut = task({ keepLatest: true }, async () => {
    if (!this.features.isEnabled('cdp_enabled')) {
      return false;
    }

    try {
      await this.store.queryRecord('integrations/cdp-out', {});
      return true;
    } catch (e) {
      return false;
    }
  });

  fetchWarehouseSync = task({ keepLatest: true }, async () => {
    try {
      return await this.store.queryRecord('warehouse-sync', {});
    } catch (e) {
      return null;
    }
  });

  fetchSQLImports = task({ keepLatest: true }, async () => {
    let importsPromises = await hashSettled({
      snowflake: this.store.query('sql-import', { database: SqlImportDatabase.snowflake }),
      bigquery: this.store.query('sql-import', { database: SqlImportDatabase.bigQuery }),
      mysql: this.store.query('sql-import', { database: SqlImportDatabase.mysql }),
      sqlserver: this.store.query('sql-import', { database: SqlImportDatabase.sqlserver }),
      postgres: this.store.query('sql-import', { database: SqlImportDatabase.postgres }),
      redshift: this.store.query('sql-import', { database: SqlImportDatabase.redshift }),
    });

    // return lists of each SQL import integration, fallback to empty array in case of an API error:
    return Object.entries(importsPromises).reduce(
      (aggr, [key, promiseResult]) => {
        return { ...aggr, [key]: promiseResult.state === 'fulfilled' ? promiseResult.value.toArray() : [] };
      },
      {} as Record<SqlImportDatabase, SqlImportModel[]>,
    );
  });

  fetchAPIKeys = task({ keepLatest: true }, async () => {
    try {
      return await this.apiCredentials.loadCurrentEnvironmentCredentials.perform();
    } catch (e) {
      return null;
    }
  });

  loadForms = task({ keepLatest: true }, async () => {
    await all([this.facebookLeadAds.initialize(), this.customForms.initialize()]);
    let total: number = this.facebookLeadAds.connectedForms.length + this.customForms.connectedOrDraftForms.length;

    return {
      facebookLeadAds: this.facebookLeadAds.enabled,
      customForms: this.customForms.connectedOrDraftForms.filter((f) => f.isScannedForm || f.isApiForm).length > 0,
      typeform: this.customForms.connectedOrDraftForms.filter((f) => f.isTypeform).length > 0,
      jotform: this.customForms.connectedOrDraftForms.filter((f) => f.isJotform).length > 0,
      total,
    };
  });

  @action
  onNewIntegrationClick() {
    let trackingData: Record<string, string> = {
      from: this.from,
      requestType: 'New',
    };

    if (this.selectedCategory) {
      trackingData.category = this.selectedCategory;
    }

    if (this.selectedType) {
      trackingData.type = this.selectedType;
    }

    this.analytics.track('integration', 'request_started', trackingData);

    this.set('modal', NewIntegrationModalState.Form);
  }

  @action
  onNewIntegrationClose() {
    this.set('newIntegrationRequest', EmObject.create({}));
    this.set('modal', NewIntegrationModalState.None);
  }

  @action
  onNewIntegrationSubmit(request: NewIntegration) {
    this.set('newIntegrationRequest', EmObject.create(request));
    this.set('modal', NewIntegrationModalState.Thanks);
  }

  @action
  searchIntegrations(value: string) {
    this.set('query', value);
    this.trackFilterEvent('search');
  }

  @action
  chooseCategory(category: string) {
    this.set('selectedCategory', category);
    this.trackFilterEvent('filter');
  }

  @action
  chooseType(type: string) {
    this.set('selectedType', type);
    this.trackFilterEvent('type');
  }
}
